import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from '@increasecard/icons';
import { Field } from '@increasecard/typed-components/dist/types/Field';
import { Filter, FilterButton } from '@increasecard/typed-components';
import { InlineAmount, InlineAmountProps } from './InlineAmount';
import { Calendar } from '../Calendar/Calendar';
import { localizedFullMonthNameAndYear, asISODateString, LoadingOverlay } from '@card/commonlib';
import { CalendarToggle } from './CalendarToggle';
import { useCalendarSection, MonthAndYear } from './useCalendarSection';
import {
  Container,
  Sidebar,
  CalendarContainer,
  CalendarHeader,
  ChangeMonthButton,
  ChangeMonthMonthName,
  ChangeMonthWrapper
} from './CalendarSectionStyles';
import {
  isSameMonth,
  isWeekend,
  isFuture,
  eachDayOfInterval,
  startOfWeek,
  differenceInDays,
  parseISO
} from 'date-fns';
import { CalendarEventList } from '../Calendar/CalendarStyles';
import {
  DayConfirmedSalesAmount,
  WeekSalesToCollectAmount,
  WeekConfirmedSalesAmount,
  DaySalesToCollectAmount
} from './CalendarAmount';
import { CalendarData } from '../../useCalendarData';
import { useTranslation } from 'react-i18next';
import { Loadable, LoadableStatus } from '@card/types';

const SALES_TO_COLLECT_FUTURE_DAYS_THRESHOLD = 30;

type MonthTotalProps = Pick<InlineAmountProps, 'loading' | 'title' | 'value'>;

function ConfirmedSalesMonthTotal({ loading, value, title }: MonthTotalProps) {
  return <InlineAmount loading={loading} title={title} value={value} variant="dark70" />;
}

function SalesToCollectMonthTotal({ loading, value, title }: MonthTotalProps) {
  return <InlineAmount loading={loading} title={title} value={value} variant="dark30" />;
}

export interface CalendarSectionProps {
  soldAmounts: Loadable<CalendarData>;
  collectedAmounts: Loadable<CalendarData>;
  monthAndYear: MonthAndYear;
  onChangeMonthAndYear: (monthAndYear: MonthAndYear) => void;
  filters: Record<string, Filter>;
  setFilters: (filters: Record<string, Filter>) => void;
  filterFields: Field[];
}

export function CalendarSection({
  monthAndYear,
  onChangeMonthAndYear,
  soldAmounts,
  collectedAmounts,
  filters,
  setFilters,
  filterFields
}: CalendarSectionProps) {
  const { t } = useTranslation('dashboard');
  const {
    referenceDate,
    goToNextMonth,
    goToPreviousMonth,
    showConfirmedSales,
    showSalesToCollect,
    toggleShowConfirmedSales,
    toggleShowSalesToCollect
  } = useCalendarSection({ monthAndYear, onChangeMonthAndYear });

  const isLoading = [soldAmounts.status, collectedAmounts.status].includes(LoadableStatus.Fetching);

  const isReferenceMonth = (date: Date) => isSameMonth(date, referenceDate);

  const sumCalendarData = (data: CalendarData) =>
    Object.entries(data)
      .filter(([dateString]) => isSameMonth(parseISO(`${dateString}T00:00-0300`), referenceDate))
      .map(([, value]) => value)
      .map((amount) => amount?.value || 0)
      .reduce((a, b) => a + b, 0);

  const sumCalendarDataForWeek = (data: CalendarData, date: Date) =>
    eachDayOfInterval({ start: startOfWeek(date), end: date })
      .filter(isReferenceMonth)
      .map((d) => data[asISODateString(d)]?.value || 0)
      .reduce((prev, curr) => prev + curr, 0);

  const today = new Date();

  const weeklySold = (date: Date) =>
    soldAmounts.status === LoadableStatus.Fetched && sumCalendarDataForWeek(soldAmounts.data, date);

  const weeklyCollected = (date: Date) =>
    collectedAmounts.status === LoadableStatus.Fetched &&
    sumCalendarDataForWeek(collectedAmounts.data, date);

  const totalSold =
    soldAmounts.status === LoadableStatus.Fetched && sumCalendarData(soldAmounts.data);

  const totalCollected =
    collectedAmounts.status === LoadableStatus.Fetched && sumCalendarData(collectedAmounts.data);

  const isBelowSalesToCollectThreshold = (date: Date) =>
    differenceInDays(date, today) < SALES_TO_COLLECT_FUTURE_DAYS_THRESHOLD;

  return (
    <Container>
      <Sidebar>
        <ChangeMonthWrapper>
          <ChangeMonthButton
            aria-label={t('calendarSection.goToPreviousMonth')}
            onClick={goToPreviousMonth}
            title={t('calendarSection.goToPreviousMonth')}
          >
            {/* TODO(santi698): Remove margin after fixing apparent out-of-center in
                                increasecard/icons
              */}
            <ArrowLeftIcon height="24" style={{ marginRight: '1px' }} width="24" />
          </ChangeMonthButton>
          <ChangeMonthMonthName>
            {localizedFullMonthNameAndYear(referenceDate)}
          </ChangeMonthMonthName>
          <ChangeMonthButton
            aria-label={t('calendarSection.goToNextMonth')}
            onClick={goToNextMonth}
            title={t('calendarSection.goToNextMonth')}
          >
            {/* TODO(santi698): Remove margin after fixing apparent out-of-center in
                                increasecard/icons
              */}
            <ArrowRightIcon height="24" style={{ marginLeft: '1px' }} width="24" />
          </ChangeMonthButton>
        </ChangeMonthWrapper>
        <div style={{ marginBottom: '64px' }}>
          <CalendarToggle
            checked={showConfirmedSales}
            description={t('calendarSection.confirmedSalesDescription')}
            id="showConfirmedSales"
            label={t('calendarSection.confirmedSales')}
            name="showConfirmedSales"
            onChange={toggleShowConfirmedSales}
            variant="light"
          />
          <ConfirmedSalesMonthTotal
            loading={collectedAmounts.status === LoadableStatus.Fetching}
            title={t('calendarSection.confirmedSalesMonthlyTotal')}
            value={totalSold || 0}
          />
        </div>
        <div>
          <CalendarToggle
            checked={showSalesToCollect}
            description={t('calendarSection.salesToCollectDescription')}
            id="showSalesToCollect"
            label={t('calendarSection.salesToCollect')}
            name="showSalesToCollect"
            onChange={toggleShowSalesToCollect}
            variant="dark30"
          />
          <SalesToCollectMonthTotal
            loading={collectedAmounts.status === LoadableStatus.Fetching}
            title={t('calendarSection.salesToCollectMonthlyTotal')}
            value={totalCollected || 0}
          />
        </div>
      </Sidebar>
      <CalendarContainer>
        <CalendarHeader>
          {filterFields ? (
            <FilterButton fields={filterFields as Field[]} onChange={setFilters} value={filters} />
          ) : (
            <FilterButton fields={[]} onChange={setFilters} value={filters} />
          )}
        </CalendarHeader>
        <LoadingOverlay active={isLoading} style={{ top: '1px', left: '-1px' }}>
          <Calendar
            month={referenceDate.getMonth()}
            renderDay={({ day, isCurrentMonth }) => {
              if (!isCurrentMonth) return null;
              const isoDateString = asISODateString(day);
              const soldValue =
                (soldAmounts.status === LoadableStatus.Fetched &&
                  soldAmounts.data[isoDateString]?.value) ||
                0;
              const collectedValue =
                (collectedAmounts.status === LoadableStatus.Fetched &&
                  collectedAmounts.data[isoDateString]?.value) ||
                0;
              return (
                <CalendarEventList>
                  <DayConfirmedSalesAmount
                    day={day}
                    hidden={!showConfirmedSales || isFuture(day)}
                    value={soldValue}
                  />
                  <DaySalesToCollectAmount
                    day={day}
                    hidden={
                      !showSalesToCollect || isWeekend(day) || !isBelowSalesToCollectThreshold(day)
                    }
                    value={collectedValue}
                  />
                </CalendarEventList>
              );
            }}
            renderWeeklySummary={({ day, weekOfMonthNumber }) => (
              <CalendarEventList>
                <WeekConfirmedSalesAmount
                  day={day}
                  hidden={!showConfirmedSales || isFuture(startOfWeek(day))}
                  value={weeklySold(day) || 0}
                  week={weekOfMonthNumber}
                />
                <WeekSalesToCollectAmount
                  day={day}
                  hidden={!showSalesToCollect || !isBelowSalesToCollectThreshold(startOfWeek(day))}
                  value={weeklyCollected(day) || 0}
                  week={weekOfMonthNumber}
                />
              </CalendarEventList>
            )}
            year={referenceDate.getFullYear()}
          />
        </LoadingOverlay>
      </CalendarContainer>
    </Container>
  );
}
